import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { KNOWN_ERROR_CODES } from 'receivable-invoices-js/react/constants/mandates';
import { apiBaseURL } from 'receivable-invoices-js/constants/hosts';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

export interface SignMandateParams {
  mandateId: string;
  clientIban: string;
  clientAddress?: string;
  clientCity?: string;
  clientZipCode?: string;
  clientProvinceCode?: string;
  clientCountryCode?: string;
  deviceId?: string;
}

interface ValidationError {
  source: {
    pointer: string;
  };
  code: string;
}

interface SignMandateErrorResponse {
  errors: ValidationError[];
}

interface SignMandateData {
  data: {
    type: 'direct-debit-collection-mandates';
    attributes: {
      client_iban: string;
      client_snapshot: {
        address?: string;
        city?: string;
        zip_code?: string;
        province_code?: string;
        country_code?: string;
      };
    };
  };
}

export class SignMandateValidationError extends Error {
  constructor(
    public errors: ValidationError[],
    message = 'Mandate signing validation failed'
  ) {
    super(message);
    this.name = 'SignMandateValidationError';
  }
}

// Check if error is validation error with the expected format
function isValidationError(error: unknown): error is SignMandateErrorResponse {
  return (
    error !== null &&
    typeof error === 'object' &&
    'errors' in error &&
    Array.isArray((error as SignMandateErrorResponse).errors)
  );
}

// Check if error contains known error codes
function isKnownError(errors: ValidationError[]): boolean {
  return errors.some((error: ValidationError) => KNOWN_ERROR_CODES.includes(error.code));
}

const URL = `${apiBaseURL}/v1/direct_debit_collection_mandates/shared`;

export async function signMandate(params: SignMandateParams): Promise<boolean> {
  const {
    mandateId,
    clientIban,
    clientAddress,
    clientCity,
    clientZipCode,
    clientProvinceCode,
    clientCountryCode,
    deviceId,
  } = params;

  const clientSnapshot: SignMandateData['data']['attributes']['client_snapshot'] = {};

  if (clientAddress) clientSnapshot.address = clientAddress;
  if (clientCity) clientSnapshot.city = clientCity;
  if (clientZipCode) clientSnapshot.zip_code = clientZipCode;
  if (clientProvinceCode) clientSnapshot.province_code = clientProvinceCode;
  if (clientCountryCode) clientSnapshot.country_code = clientCountryCode;

  const data: SignMandateData = {
    data: {
      type: 'direct-debit-collection-mandates',
      attributes: {
        client_iban: clientIban,
        client_snapshot: clientSnapshot,
      },
    },
  };

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (deviceId) {
    headers['X-Qonto-Device'] = deviceId;
  }

  try {
    await qontoClient(`${URL}/${mandateId}/sign`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers,
    });

    return true;
  } catch (error: unknown) {
    // Check if it's a 422 validation error with the expected format
    if (
      error &&
      typeof error === 'object' &&
      'status' in error &&
      'data' in error &&
      isValidationError(error.data)
    ) {
      const errors = error.data.errors;

      // Only handle known error types as validation errors
      if (isKnownError(errors)) {
        throw new SignMandateValidationError(errors);
      }
    }

    throw error;
  }
}

export type SignMandateResponse = Awaited<ReturnType<typeof signMandate>>;

export function useSignMandate(
  options?: UseMutationOptions<SignMandateResponse, Error, SignMandateParams>
) {
  return useMutation({
    mutationFn: signMandate,
    ...options,
  });
}
